import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Table, Form, InputGroup,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../Components/Error';
import ListPager from '../../Components/ListPager';
import OperatorInputModal from './Components/OperatorInputModal';
import DriverManagementModal from './Components/DriverManagementModal';
import FleetManagementModal from './Components/FleetManagementModal';
import PortalUsersModal from './Components/PortalUsersModal';
import OperatorPricingProfileEditor from './Components/OperatorPricingProfileEditor';
import AddSettlementCurrencyModal from './Components/AddSettlementCurrencyModal';
import OperatorGroupRow from './Components/OperatorGroupRow';
import CounterpartyDocumentsModal from '../../Components/KnowledgeBase/CounterpartyDocumentsModal';
import useModalState from '../../Hooks/useModalState';
import UserService from '../../Services/UserService';
import Permissions from '../../Utils/Permissions';
import usePagedList from '../../Hooks/usePagedList';
import OperatorService from '../../Services/OperatorService';
import { groupOperators, matchesGroup } from './operatorGroups';

function Operators() {
  const [operators, setOperators] = useState([]);
  // Whether the collection has actually arrived. An empty list mid-fetch must
  // not be mistaken for "page 3 does not exist" — see usePagedList.
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  // Modals
  const operatorInputModal = useModalState();
  const driverManagementModal = useModalState();
  const fleetManagementModal = useModalState();
  const pricingProfileModal = useModalState();
  const portalUsersModal = useModalState();
  const addCurrencyModal = useModalState();
  const documentsModal = useModalState();

  // Gates the Documents row action AND the upload/delete inside the modal.
  const canManageKnowledgeBase = UserService.getInstance()
    .canAccessModule(Permissions.MANAGE_AI);

  const fetchOperators = async () => {
    try {
      const fetchedOperators = await OperatorService.fetchOperators();
      setOperators(fetchedOperators?.data?.data);
    } catch (err) {
      setError(error);
    } finally {
      setLoaded(true);
    }
  };

  const debouncedFetchOperators = _.debounce(() => {
    fetchOperators();
  }, 500);

  useEffect(() => {
    debouncedFetchOperators();
    return () => debouncedFetchOperators.cancel();
  }, []);

  /**
   * One row per company, not per record: a company that settles in two
   * currencies is two Operator records, and paging over the RECORDS would
   * make a page of 25 show 24 companies while claiming 25. The pager counts
   * what the table shows, so it counts groups.
   */
  const groups = useMemo(() => groupOperators(operators), [operators]);

  const {
    view, rows, setFilter, setPage, setSize, pagination,
  } = usePagedList({ items: groups, matches: matchesGroup, loaded });

  const ROW_ACTIONS = {
    edit: operatorInputModal,
    drivers: driverManagementModal,
    fleet: fleetManagementModal,
    pricing: pricingProfileModal,
    portalUsers: portalUsersModal,
    addCurrency: addCurrencyModal,
    documents: documentsModal,
  };

  const handleRowAction = (key, operator) => ROW_ACTIONS[key].show(operator);

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Operators
              {' '}
              <span className="hero-highlight">Management</span>
            </h1>
            <p className="lead text-muted mb-4">
              Manage system operators and their access
            </p>
            <div className="hero-actions">
              <Button
                onClick={() => operatorInputModal.show({})}
                size="lg"
                variant="primary"
              >
                <i className="fa fa-plus me-2" />
                Add New Operator
              </Button>
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-users fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      <div className="list-search mb-3">
        <InputGroup>
          <InputGroup.Text className="list-search-icon">
            <i className="fa fa-search" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search operators by name, email, phone…"
            value={view.search}
            onChange={(e) => setFilter({ search: e.target.value })}
          />
          {view.search && (
            <Button variant="outline-secondary" onClick={() => setFilter({ search: '' })} aria-label="Clear search">
              <i className="fa fa-times" />
            </Button>
          )}
        </InputGroup>
      </div>

      <Table responsive striped bordered hover className="mt-2 responsive-card-table">
        <thead>
          <tr>
            {/* Website, Settlement Currency and Monthly Report are off the
                table so the rows that matter fit without scrolling. The
                currency badges moved next to the name rather than going
                with their column — on a grouped company they choose which
                record the row's actions open. */}
            <th>Name</th>
            <th>E-mail address</th>
            <th>Phone number</th>
            <th>Active</th>
            <th>Daily Digest</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted py-4">
                {view.search ? 'No operators match your search.' : 'No operators found.'}
              </td>
            </tr>
          )}
          {rows.map((group) => (
            <OperatorGroupRow key={group.key} group={group} onAction={handleRowAction} />
          ))}
        </tbody>
      </Table>

      <ListPager
        pagination={pagination}
        onPageChange={setPage}
        onLimitChange={setSize}
      />

      {/* The modals are the screen's, not the row's: each holds the operator
          it was opened with, so turning a page underneath one — or filtering
          its row away — leaves the open modal alone. */}
      {operatorInputModal.isVisible && (
        <OperatorInputModal
          originalOperator={operatorInputModal.selectedObject}
          show={operatorInputModal.isVisible}
          fetchOperators={fetchOperators}
          handleClose={operatorInputModal.hide}
        />
      )}

      {/* Handed the WHOLE collection too: a company that settles in two
          currencies keeps two operator records, and the same human then holds
          a driver row under each. The modal needs the list to find the
          record's siblings and to name the record every row sits on. */}
      {driverManagementModal.isVisible && (
        <DriverManagementModal
          operator={driverManagementModal.selectedObject}
          operators={operators}
          show={driverManagementModal.isVisible}
          handleClose={driverManagementModal.hide}
        />
      )}

      {/* Handed the WHOLE collection as well as the row: a vehicle's expense
          routing points at OTHER operator records (the cost buckets), so the
          modal needs the list to offer them and to name the ones already
          mapped. */}
      {fleetManagementModal.isVisible && (
        <FleetManagementModal
          operator={fleetManagementModal.selectedObject}
          operators={operators}
          show={fleetManagementModal.isVisible}
          handleClose={fleetManagementModal.hide}
        />
      )}

      {pricingProfileModal.isVisible && (
        <OperatorPricingProfileEditor
          operator={pricingProfileModal.selectedObject}
          show={pricingProfileModal.isVisible}
          handleClose={pricingProfileModal.hide}
        />
      )}

      {/* Unmounted when hidden, so a freshly minted enrolment token never
          outlives the modal it was shown in. */}
      {portalUsersModal.isVisible && (
        <PortalUsersModal
          operator={portalUsersModal.selectedObject}
          show={portalUsersModal.isVisible}
          handleClose={portalUsersModal.hide}
        />
      )}

      {/* This operator's own documents, pre-scoped to them: opened from their
          row, so there is no scope picker and no counterparty to choose. The
          same uploader and the same document list as the Knowledge Base
          screen — one uploader, two entry points. */}
      {documentsModal.isVisible && (
        <CounterpartyDocumentsModal
          show={documentsModal.isVisible}
          handleClose={documentsModal.hide}
          kind="operator"
          counterparty={documentsModal.selectedObject}
          canManage={canManageKnowledgeBase}
        />
      )}

      {/* Handed the WHOLE collection, not the row: whether this company can
          take another currency depends on what else is already on file. */}
      {addCurrencyModal.isVisible && (
        <AddSettlementCurrencyModal
          operator={addCurrencyModal.selectedObject}
          operators={operators}
          show={addCurrencyModal.isVisible}
          fetchOperators={fetchOperators}
          handleClose={addCurrencyModal.hide}
        />
      )}
    </>
  );
}

export default Operators;
